import { useState, useMemo, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Search,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Users,
  Plus,
  RefreshCw,
  Trash2,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { useNavigate } from 'react-router-dom';
import { usePlatformOrganizations, useDeleteOrganization } from '@/hooks/use-platform';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import { handleApiError } from '@/lib/api-client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import { toast } from '@/app/components/ui/simple-toaster';
import type { PlatformOrganizationListItem } from '@/lib/types';

type SortField = 'name' | 'user_count' | 'created_at';
type SortDir = 'asc' | 'desc';

function TableSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 p-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-4 w-24" />
        </div>
      ))}
    </div>
  );
}

function SortIcon({ field, current, direction }: { field: SortField; current: SortField; direction: SortDir }) {
  if (field !== current) return <ChevronsUpDown className="h-3.5 w-3.5 text-gray-400" />;
  return direction === 'asc' ? (
    <ChevronUp className="h-3.5 w-3.5" />
  ) : (
    <ChevronDown className="h-3.5 w-3.5" />
  );
}

export function PlatformAdminOrganizationsPage() {
  const orgTimeZone = useOrgTimeZone();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const queryParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (statusFilter === 'active') params.is_active = 'true';
    if (statusFilter === 'inactive') params.is_active = 'false';
    return params;
  }, [debouncedSearch, statusFilter]);

  const { data, isLoading, isError, refetch } = usePlatformOrganizations(queryParams);
  const deleteOrg = useDeleteOrganization();
  const [deleteTarget, setDeleteTarget] = useState<PlatformOrganizationListItem | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const handleDeleteOrg = () => {
    if (!deleteTarget) return;
    deleteOrg.mutate({ id: deleteTarget.id, confirmation: deleteConfirmation }, {
      onSuccess: () => {
        toast.success(`Organization "${deleteTarget.name}" has been permanently deleted.`);
        setDeleteOpen(false);
        setDeleteTarget(null);
        setDeleteConfirmation('');
      },
      onError: (error) => {
        toast.error(handleApiError(error).message);
      },
    });
  };

  const handleDeleteOpenChange = (open: boolean) => {
    setDeleteOpen(open);
    if (!open) {
      setDeleteTarget(null);
      setDeleteConfirmation('');
    }
  };

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const sortedResults = useMemo(() => {
    if (!data?.results) return [];
    const results = [...data.results];
    results.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'user_count':
          cmp = a.user_count - b.user_count;
          break;
        case 'created_at':
          cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return results;
  }, [data?.results, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Organizations</h1>
          <p className="text-gray-600 mt-1">
            {data ? `${data.count} organization${data.count !== 1 ? 's' : ''}` : 'Manage all platform organizations'}
          </p>
        </div>
        <Button asChild>
          <Link to="/platform-admin/organizations/new">
            <Plus className="h-4 w-4 mr-1" />
            Create Organization
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2">
          {(['all', 'active', 'inactive'] as const).map((value) => (
            <Button
              key={value}
              variant={statusFilter === value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(value)}
            >
              {value.charAt(0).toUpperCase() + value.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="pb-0">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Organizations
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {isLoading ? (
            <TableSkeleton />
          ) : isError ? (
            <div className="py-12 text-center">
              <AlertTriangle className="h-10 w-10 text-orange-500 mx-auto mb-3" />
              <p className="text-gray-900 font-medium">Unable to load organizations</p>
              <p className="text-sm text-gray-500 mt-1">Something went wrong while fetching data.</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Retry
              </Button>
            </div>
          ) : sortedResults.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              {search || statusFilter !== 'all'
                ? 'No organizations match your filters'
                : 'No organizations yet'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 text-left">
                    <th className="pb-3 pr-4">
                      <button
                        className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        onClick={() => handleSort('name')}
                      >
                        Name
                        <SortIcon field="name" current={sortField} direction={sortDir} />
                      </button>
                    </th>
                    <th className="pb-3 pr-4">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Admin Email
                      </span>
                    </th>
                    <th className="pb-3 pr-4">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </span>
                    </th>
                    <th className="pb-3 pr-4">
                      <button
                        className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        onClick={() => handleSort('user_count')}
                      >
                        <Users className="h-3.5 w-3.5" />
                        Users
                        <SortIcon field="user_count" current={sortField} direction={sortDir} />
                      </button>
                    </th>
                    <th className="pb-3 pr-4">
                      <button
                        className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        onClick={() => handleSort('created_at')}
                      >
                        Created ({orgTimeZone})
                        <SortIcon field="created_at" current={sortField} direction={sortDir} />
                      </button>
                    </th>
                    <th className="pb-3">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedResults.map((org) => (
                    <tr
                      key={org.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/platform-admin/organizations/${org.id}`)}
                    >
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900">{org.name}</p>
                          {org.is_demo && (
                            <Badge
                              variant="outline"
                              className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-50"
                            >
                              Demo
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <p className="text-sm text-gray-600">{org.admin_email ?? '—'}</p>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge
                          variant={org.is_active ? 'default' : 'secondary'}
                          className={
                            org.is_active
                              ? 'bg-green-100 text-green-800 hover:bg-green-100'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-100'
                          }
                        >
                          {org.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4">
                        <span className="text-sm text-gray-600">{org.user_count}</span>
                      </td>
                      <td className="py-3 pr-4">
                        <span className="text-sm text-gray-500">
                          {formatDateTimeInTimeZone(org.created_at, orgTimeZone)}
                        </span>
                      </td>
                      <td className="py-3">
                        {org.is_demo && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete Organization"
                            onClick={(e: MouseEvent) => {
                              e.stopPropagation();
                              setDeleteTarget(org);
                              setDeleteConfirmation('');
                              setDeleteOpen(true);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={handleDeleteOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Organization</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently deleting <strong>{deleteTarget?.name}</strong> removes the demo organization and all associated users, data, and subscriptions. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="list-delete-organization-confirmation">
              Type <strong>{deleteTarget?.name}</strong> to confirm
            </Label>
            <Input
              id="list-delete-organization-confirmation"
              value={deleteConfirmation}
              onChange={(event) => setDeleteConfirmation(event.target.value)}
              autoComplete="off"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteOrg.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteOrg}
              disabled={
                deleteOrg.isPending ||
                !deleteTarget ||
                deleteConfirmation !== deleteTarget.name
              }
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleteOrg.isPending ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Deleting...</>
              ) : (
                'Delete Permanently'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
